import { useCallback, useMemo, useRef, useState } from "react";
import {
  checkAiAuth,
  continueAiSession,
  continueAiChat,
  deleteAiUserMessage,
  editAiUserMessage,
  endAiChat,
  getAiSessionState,
  listAiSessions,
  resumeAiChat,
  sendAiChatMessage,
  startAiChat,
  transcribeAiAudio,
} from "../../api/sherpa/sherpaApi.js";

const nowMessage = (role, content, flags = [], messageId = null) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  messageId,
  role,
  content,
  flags,
  createdAt: new Date().toISOString(),
});

export const mapMessagesFromResponse = (chatData) => {
  if (Array.isArray(chatData?.messages) && chatData.messages.length > 0) {
    return chatData.messages.map((m) => {
      const isAssistant = m.role === "assistant" || m.isCoach;
      return {
        id: `msg-${m.id || m.message_id || Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        messageId: m.id || m.message_id || null,
        role: isAssistant ? "assistant" : "user",
        content: m.content || m.text || "",
        flags: m.flags || [],
        createdAt: m.created_at || new Date().toISOString(),
      };
    });
  }

  if (chatData?.greeting) {
    return [
      {
        id: `assistant-greeting-${Date.now()}`,
        role: "assistant",
        content: chatData.greeting,
        flags: [],
        createdAt: new Date().toISOString(),
      },
    ];
  }

  return [];
};

const ACTIVE_CHAT_KEY = "mindsherpa_active_chat_id";
const ACTIVE_SESSION_KEY = "mindsherpa_active_session_id";
const ACTIVE_SEEN_AT_KEY = "mindsherpa_active_chat_seen_at";

// Matches the service's default inactivity close. A conversation used within
// this window is reopened on the next visit; an older one stays in history.
const RECENT_CHAT_WINDOW_MS = 30 * 60 * 1000;

// A chat the service has just closed is still being saved for a short while
// and cannot be reopened until that finishes, so a 404 is retried.
const REOPEN_RETRY_DELAYS_MS = [2000, 3000, 5000, 10000, 10000, 15000];
const STARTUP_REOPEN_RETRY_DELAYS_MS = [2000, 4000];

const rememberActiveChat = (chatId, dbSessionId) => {
  localStorage.setItem(ACTIVE_CHAT_KEY, chatId);
  if (dbSessionId) localStorage.setItem(ACTIVE_SESSION_KEY, String(dbSessionId));
  else localStorage.removeItem(ACTIVE_SESSION_KEY);
  localStorage.setItem(ACTIVE_SEEN_AT_KEY, String(Date.now()));
};

const forgetActiveChat = () => {
  localStorage.removeItem(ACTIVE_CHAT_KEY);
  localStorage.removeItem(ACTIVE_SESSION_KEY);
  localStorage.removeItem(ACTIVE_SEEN_AT_KEY);
};

const markActiveChatSeen = () =>
  localStorage.setItem(ACTIVE_SEEN_AT_KEY, String(Date.now()));

const wasActiveRecently = () =>
  Date.now() - Number(localStorage.getItem(ACTIVE_SEEN_AT_KEY) || 0) <
  RECENT_CHAT_WINDOW_MS;

// The service answers 404 when a live chat is gone: closed after inactivity
// or a hidden tab, replaced by another tab, or lost in a restart. A network
// error, timeout or 5xx says nothing about whether the chat still exists.
const CONVERSATION_PAUSED = "conversation-paused";

const isChatGone = (error) => error?.response?.status === 404;
const isOutcomeUnknown = (error) =>
  error?.code !== CONVERSATION_PAUSED &&
  (!error?.response || error.response.status >= 500);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const reopenConversation = async (dbSessionId, retryDelaysMs) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await continueAiSession({ sessionId: dbSessionId });
    } catch (error) {
      if (!isChatGone(error) || attempt >= retryDelaysMs.length) throw error;
      await wait(retryDelaysMs[attempt]);
    }
  }
};

const describeSendFailure = (error) => {
  if (error?.code === CONVERSATION_PAUSED) {
    return "Sherpa couldn't reconnect to this conversation. Your messages are saved. Please try again in a moment, or start a new chat.";
  }
  const status = error?.response?.status;
  if (status === 429) {
    return "You're sending messages quickly. Please wait a moment, then try again.";
  }
  if (status === 400) {
    return "That message couldn't be sent. If it is very long, please shorten it and try again.";
  }
  return "Sherpa couldn't reply just now. Your conversation is still here, so please try sending again.";
};

const lastOf = (items, predicate) => [...items].reverse().find(predicate);

// Pulls the interrupted chat's id out of the various field names the
// session/state endpoint might use. `0`/"0" and other falsy values mean
// "no interrupted session".
const extractInterruptedChatId = (state) => {
  const candidate =
    state?.interrupted_chat_id ||
    state?.interrupted_session_id ||
    state?.interruptedSessionId ||
    state?.interruptedChatId ||
    state?.chat_id ||
    state?.chatId;

  if (!candidate || candidate === "0" || candidate === 0) return null;
  return String(candidate);
};

export const useSherpaChat = ({
  currentSession,
  updateSessionMessages,
  updateSessionMeta,
  onAuthError,
  reopenRetryDelaysMs = REOPEN_RETRY_DELAYS_MS,
}) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [shouldClose, setShouldClose] = useState(false);
  const [isCrisis, setIsCrisis] = useState(false);
  const isCheckingRef = useRef(false);

  const sessionId = currentSession?.id;
  const messages = useMemo(() => currentSession?.messages || [], [currentSession]);

  /**
   * Runs the exact mobile lifecycle in sequential order:
   * 1. POST /api/auth/check
   * 2. POST /api/session/state
   * 3. GET /api/sessions?token=...
   * 4. POST /api/chat/resume (if cached active chatId)
   * 5. Route to disclaimer, interrupted prompt, or auto-start fresh
   */
  const checkInitialState = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    setIsInitializing(true);
    setError(null);

    try {
      // Step 1: Check Auth Token
      const authRes = await checkAiAuth();
      if (authRes && authRes.valid === false) {
        const authErr = new Error("Authentication session expired. Please log in again.");
        onAuthError?.();
        throw authErr;
      }

      // Step 2: Get Session State
      const arrivalState = await getAiSessionState();
      if (!arrivalState) {
        throw new Error("Unable to retrieve session arrival state.");
      }

      // Step 3: Fetch Past Sessions List
      const pastSessionsData = await listAiSessions();
      const rawSessions = Array.isArray(pastSessionsData)
        ? pastSessionsData
        : pastSessionsData?.sessions || [];

      // Step 4: Check if we have a cached active chatId in localStorage
      const cachedChatId = localStorage.getItem(ACTIVE_CHAT_KEY);
      const cachedDbSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
      if (cachedChatId) {
        let resumeRes = null;
        try {
          resumeRes = await resumeAiChat({ chatId: cachedChatId });
        } catch (resumeErr) {
          // Only a 404 means the chat is gone. Anything else surfaces as a
          // retryable error with the cached chat kept, never as a new chat.
          if (!isChatGone(resumeErr)) throw resumeErr;
        }
        if (!resumeRes && cachedDbSessionId && wasActiveRecently()) {
          try {
            resumeRes = await reopenConversation(
              cachedDbSessionId,
              STARTUP_REOPEN_RETRY_DELAYS_MS
            );
          } catch (reopenErr) {
            if (!isChatGone(reopenErr)) throw reopenErr;
          }
        }
        const activeChatId = resumeRes?.chat_id || resumeRes?.chatId;
        if (activeChatId) {
          rememberActiveChat(
            activeChatId,
            resumeRes.db_session_id || resumeRes.dbSessionId || cachedDbSessionId
          );
          return {
            type: "resumed",
            arrivalState,
            rawSessions,
            chatData: resumeRes,
            chatId: activeChatId,
          };
        }
        forgetActiveChat();
      }

      // Step 5: Evaluate Arrival State
      const isFirstTime = Boolean(
        arrivalState?.first_time ?? arrivalState?.firstTime
      );
      const hasInterrupted = Boolean(
        arrivalState?.has_interrupted_session ?? arrivalState?.hasInterruptedSession
      );
      const interruptedChatId = extractInterruptedChatId(arrivalState);

      if (isFirstTime) {
        return {
          type: "disclaimer",
          arrivalState,
          rawSessions,
        };
      }

      if (hasInterrupted && interruptedChatId) {
        return {
          type: "interrupted",
          arrivalState,
          rawSessions,
          interruptedChatId,
        };
      }

      // Step 6: Start fresh session automatically
      const startRes = await startAiChat({ intent: "fresh" });
      const activeChatId = startRes?.chat_id || startRes?.chatId;
      if (activeChatId) {
        rememberActiveChat(
          activeChatId,
          startRes?.db_session_id || startRes?.dbSessionId
        );
      }

      return {
        type: "fresh",
        arrivalState,
        rawSessions,
        chatData: startRes,
        chatId: activeChatId,
      };
    } catch (err) {
      setError(err);
      const isAuthFailure =
        String(err?.message || "").includes("Authentication") ||
        err?.response?.status === 401;
      if (isAuthFailure) {
        onAuthError?.();
      }
      throw err;
    } finally {
      setIsInitializing(false);
      isCheckingRef.current = false;
    }
  }, [onAuthError]);

  // Opens a remote chat without touching isSending, so sendMessage can use it
  // mid-send without re-enabling the composer.
  const openRemoteChat = useCallback(
    async ({ topics = [] } = {}) => {
      const sessionData = await startAiChat({ intent: "fresh", topics });
      const remoteSessionId = sessionData?.chat_id || sessionData?.chatId;
      if (!remoteSessionId) {
        throw new Error("Unable to initialize Sherpa session.");
      }

      const dbSessionId = sessionData?.db_session_id || sessionData?.dbSessionId || null;
      rememberActiveChat(remoteSessionId, dbSessionId);

      const mappedMessages = mapMessagesFromResponse(sessionData);

      if (sessionId) {
        updateSessionMeta(sessionId, {
          remoteSessionId,
          dbSessionId,
          idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
        });
        updateSessionMessages(sessionId, mappedMessages);
      }

      return { remoteSessionId, sessionData, mappedMessages };
    },
    [sessionId, updateSessionMessages, updateSessionMeta]
  );

  const startFreshChat = useCallback(
    async (options) => {
      setIsSending(true);
      setError(null);
      try {
        return await openRemoteChat(options);
      } finally {
        setIsSending(false);
      }
    },
    [openRemoteChat]
  );

  const resumeChat = useCallback(
    async ({ chatId }) => {
      if (!chatId) throw new Error("No chat id to resume.");

      setIsSending(true);
      setError(null);
      try {
        const sessionData = await resumeAiChat({ chatId });
        const remoteSessionId = sessionData?.chat_id || sessionData?.chatId || chatId;

        rememberActiveChat(
          remoteSessionId,
          sessionData?.db_session_id || sessionData?.dbSessionId
        );

        const mappedMessages = mapMessagesFromResponse(sessionData);

        if (sessionId) {
          updateSessionMeta(sessionId, {
            remoteSessionId,
            dbSessionId: sessionData?.db_session_id || sessionData?.dbSessionId || null,
            idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
          });
          updateSessionMessages(sessionId, mappedMessages);
        }

        return { remoteSessionId, sessionData, mappedMessages };
      } finally {
        setIsSending(false);
      }
    },
    [sessionId, updateSessionMessages, updateSessionMeta]
  );

  const continuePastChat = useCallback(
    async () => {
      const archivedSessionId = currentSession?.archivedSessionId;
      if (!sessionId || !archivedSessionId || !currentSession?.readOnly) {
        throw new Error("Select a past conversation to continue.");
      }
      setIsSending(true);
      setError(null);
      try {
        const sessionData = await continueAiSession({ sessionId: archivedSessionId });
        const remoteSessionId = sessionData?.chat_id || sessionData?.chatId;
        if (!remoteSessionId) throw new Error("Unable to continue this conversation.");
        rememberActiveChat(remoteSessionId, archivedSessionId);
        updateSessionMeta(sessionId, {
          remoteSessionId,
          dbSessionId: archivedSessionId,
          archivedSessionId: null,
          isArchived: true,
          readOnly: false,
          continuedFromHistory: true,
          idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
        });
        const mappedMessages = mapMessagesFromResponse(sessionData);
        updateSessionMessages(sessionId, mappedMessages);
        return { remoteSessionId, sessionData, mappedMessages };
      } catch (requestError) {
        setError(requestError);
        throw requestError;
      } finally {
        setIsSending(false);
      }
    },
    [currentSession?.archivedSessionId, currentSession?.readOnly, sessionId, updateSessionMessages, updateSessionMeta]
  );

  const sendToSherpa = useCallback(async ({ remoteSessionId, prompt }) => {
    const response = await sendAiChatMessage({
      chatId: remoteSessionId,
      message: prompt,
    });

    if (!response) {
      throw new Error("No response from Sherpa.");
    }

    return {
      content: response.text || response.reply || response.content || "",
      flags: response.flags || [],
      userMessageId: response.userMessageId || response.message_id || null,
      assistantMessageId:
        response.assistantMessageId || response.message_id || null,
      shouldClose: Boolean(response.shouldClose || response.is_wrap_up),
      isCrisis: Boolean(response.is_crisis),
      isWrapUp: Boolean(response.is_wrap_up),
    };
  }, []);

  // Reattaches the visible conversation after its live chat was lost. With no
  // user message yet there is nothing to keep, so a new chat is opened; any
  // other conversation is reopened in place, or the send fails visibly.
  const reattachConversation = useCallback(async () => {
    if (!messages.some((message) => message.role === "user")) {
      const fresh = await openRemoteChat();
      return { remoteSessionId: fresh.remoteSessionId, greeting: fresh.mappedMessages };
    }

    // A saved chat reopened by this send still carries its id as
    // archivedSessionId in this render.
    const dbSessionId = currentSession?.dbSessionId || currentSession?.archivedSessionId;
    let sessionData = null;
    if (dbSessionId) {
      try {
        sessionData = await reopenConversation(dbSessionId, reopenRetryDelaysMs);
      } catch (reopenError) {
        // 409: the service waited for a save of this conversation that is
        // still running; the user can try again once it finishes.
        const stillSaving = reopenError?.response?.status === 409;
        if (!isChatGone(reopenError) && !stillSaving) throw reopenError;
      }
    }
    const remoteSessionId = sessionData?.chat_id || sessionData?.chatId;
    if (!remoteSessionId) {
      throw Object.assign(new Error("Conversation could not be reopened."), {
        code: CONVERSATION_PAUSED,
      });
    }

    rememberActiveChat(remoteSessionId, dbSessionId);
    updateSessionMeta(sessionId, {
      remoteSessionId,
      idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
    });
    return { remoteSessionId, greeting: null };
  }, [
    currentSession?.archivedSessionId,
    currentSession?.dbSessionId,
    messages,
    openRemoteChat,
    reopenRetryDelaysMs,
    sessionId,
    updateSessionMeta,
  ]);

  // After a failure with an unknown outcome the service may already have
  // stored a reply; show it rather than asking the user to send again.
  const findStoredReply = useCallback(
    async (remoteSessionId, prompt) => {
      try {
        const stored = mapMessagesFromResponse(
          await resumeAiChat({ chatId: remoteSessionId })
        );
        const reply = stored[stored.length - 1];
        const repliedTo = lastOf(stored, (message) => message.role === "user");
        const previousReply = lastOf(
          messages,
          (message) => message.role === "assistant" && !message.flags?.includes("system")
        );
        if (
          reply?.role === "assistant" &&
          repliedTo?.content === prompt &&
          reply.content !== previousReply?.content
        ) {
          return reply;
        }
      } catch (resyncError) {
        // The caller reports the original failure.
      }
      return null;
    },
    [messages]
  );

  const sendMessage = useCallback(
    async (textValue, { onPastChatContinued } = {}) => {
      if (!sessionId) throw new Error("No active session selected.");
      if (isSending) return;

      const prompt = textValue?.trim();
      if (!prompt) return;

      const continued = currentSession?.readOnly ? await continuePastChat() : null;
      if (continued) onPastChatContinued?.(continued.remoteSessionId);
      const previousMessages = continued?.mappedMessages || messages;

      setIsSending(true);
      setError(null);

      const userMessage = nowMessage("user", prompt);
      const nextMessages = [...previousMessages, userMessage];
      updateSessionMessages(sessionId, nextMessages);

      // 1. Check for local Crisis safety keywords (matches mobile app behavior)
      const CRISIS_KEYWORDS_REGEX =
        /\b(kill|killing|killed|kills|murder|murdering|suicide|suicidal|self-harm|self harm|harm|harms|harming|harm myself|hurt|huart|hurts|hurting|hurt myself|end my life|want to die|overdose|abuse|abusing|shoot|shooting|stab|stabbing)\b/i;

      if (CRISIS_KEYWORDS_REGEX.test(prompt)) {
        const safetyMessage = nowMessage(
          "assistant",
          "I hear that you're going through a very difficult time right now, but your safety is what matters most. I am an AI coach and cannot provide emergency help or clinical therapy. Please reach out to the professional support services pinned below, or contact a trusted person immediately.",
          ["safety", "crisis"]
        );
        updateSessionMessages(sessionId, [...nextMessages, safetyMessage]);
        setIsCrisis(true);
        setIsSending(false);
        return;
      }

      let remoteSessionId = continued?.remoteSessionId || currentSession?.remoteSessionId;
      let sentMessages = nextMessages;
      const showNewGreeting = (greeting) => {
        sentMessages = [...greeting, userMessage];
        updateSessionMessages(sessionId, sentMessages);
      };

      try {
        if (!remoteSessionId) {
          const fresh = await openRemoteChat();
          remoteSessionId = fresh.remoteSessionId;
          showNewGreeting(fresh.mappedMessages);
        }

        let parsedResponse;
        try {
          parsedResponse = await sendToSherpa({ remoteSessionId, prompt });
        } catch (sendError) {
          if (!isChatGone(sendError)) throw sendError;
          // The service checks the chat before storing anything, so after a
          // 404 this message can be sent once to the reattached chat.
          const reattached = await reattachConversation();
          remoteSessionId = reattached.remoteSessionId;
          if (reattached.greeting) showNewGreeting(reattached.greeting);
          parsedResponse = await sendToSherpa({ remoteSessionId, prompt });
        }
        markActiveChatSeen();

        const messagesWithIds = sentMessages.map((message, index) =>
          index === sentMessages.length - 1
            ? { ...message, messageId: parsedResponse.userMessageId || null }
            : message
        );

        const assistantMessage = nowMessage(
          "assistant",
          parsedResponse.content,
          parsedResponse.flags,
          parsedResponse.assistantMessageId || null
        );
        updateSessionMessages(sessionId, [...messagesWithIds, assistantMessage]);
        setShouldClose(Boolean(parsedResponse.shouldClose));
        setIsCrisis(Boolean(parsedResponse.isCrisis));
      } catch (requestError) {
        if (remoteSessionId && isOutcomeUnknown(requestError)) {
          const storedReply = await findStoredReply(remoteSessionId, prompt);
          if (storedReply) {
            markActiveChatSeen();
            updateSessionMessages(sessionId, [...sentMessages, storedReply]);
            setShouldClose(false);
            return;
          }
        }
        const failureMessage = describeSendFailure(requestError);
        updateSessionMessages(sessionId, [
          ...sentMessages,
          nowMessage("assistant", failureMessage, ["system"]),
        ]);
        setError(requestError);
        setShouldClose(false);
        const isAuthFailure =
          String(requestError?.message || "").includes("Authentication") ||
          requestError?.response?.status === 401;
        if (isAuthFailure) {
          onAuthError?.();
          throw requestError;
        }
        throw Object.assign(new Error(failureMessage), {
          cause: requestError,
          response: requestError?.response,
        });
      } finally {
        setIsSending(false);
      }
    },
    [
      currentSession?.readOnly,
      currentSession?.remoteSessionId,
      continuePastChat,
      findStoredReply,
      isSending,
      messages,
      onAuthError,
      openRemoteChat,
      reattachConversation,
      sendToSherpa,
      sessionId,
      updateSessionMessages,
    ]
  );

  const continueConversation = useCallback(async () => {
    const chatId = currentSession?.remoteSessionId;
    if (!chatId) throw new Error("No active remote chat to continue.");
    await continueAiChat({ chatId });
    setShouldClose(false);
  }, [currentSession?.remoteSessionId]);

  const endConversation = useCallback(async () => {
    const chatId = currentSession?.remoteSessionId;
    if (chatId) {
      await endAiChat({ chatId });
      forgetActiveChat();
    }
    setShouldClose(false);
  }, [currentSession?.remoteSessionId]);

  const editUserMessage = useCallback(
    async ({ messageId, text }) => {
      if (!sessionId) throw new Error("No active session selected.");
      if (currentSession?.readOnly) throw new Error("Past sessions are read-only.");
      const chatId = currentSession?.remoteSessionId;
      if (!chatId) throw new Error("No active remote chat.");
      if (!messageId) throw new Error("No message id available.");

      await editAiUserMessage({ messageId, chatId, text });
      const nextMessages = messages.map((message) =>
        message.messageId === messageId ? { ...message, content: text } : message
      );
      updateSessionMessages(sessionId, nextMessages);
    },
    [
      currentSession?.readOnly,
      currentSession?.remoteSessionId,
      messages,
      sessionId,
      updateSessionMessages,
    ]
  );

  const deleteUserMessage = useCallback(
    async ({ messageId }) => {
      if (!sessionId) throw new Error("No active session selected.");
      if (currentSession?.readOnly) throw new Error("Past sessions are read-only.");
      const chatId = currentSession?.remoteSessionId;
      if (!chatId) throw new Error("No active remote chat.");
      if (!messageId) throw new Error("No message id available.");

      await deleteAiUserMessage({ messageId, chatId });
      const nextMessages = messages.filter((message) => message.messageId !== messageId);
      updateSessionMessages(sessionId, nextMessages);
    },
    [
      currentSession?.readOnly,
      currentSession?.remoteSessionId,
      messages,
      sessionId,
      updateSessionMessages,
    ]
  );

  const transcribeAudio = useCallback(async ({ audio, audioData, format }) => {
    const response = await transcribeAiAudio({ audio, audioData, format });
    return response?.text || "";
  }, []);

  return {
    messages,
    isInitializing,
    isSending,
    error,
    shouldClose,
    isCrisis,
    dismissCrisis: () => setIsCrisis(false),
    checkInitialState,
    sendMessage,
    startFreshChat,
    resumeChat,
    continuePastChat,
    continueConversation,
    endConversation,
    editUserMessage,
    deleteUserMessage,
    transcribeAudio,
  };
};
